import React, { useEffect, useState } from 'react';
import { Liveblog, ILiveblog, INewPosts, AcceptedCookiesConfig, CommonTypings as CommonTypingsDS } from '@sky-it-cmscomposer/liveblog-design-system';
import { liveblogContainerWidget } from '@sky-it-cmscomposer/liveblog-widgets';

import skyCMP from '@sky-uk/ita-js-common-cmp';
import { IModelWrapper } from '../../typings/common-types';
import { useService } from '../hooks/useService';
import { useDateNow } from '../hooks/useDateNow';
import { setDocumentTitle, smoothScrollToTop } from '../../utils/page-utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { vendorsConfigs } from '../../utils/cmp-vendors';
import { isAuthorMode, isLocalDomain } from '../../utils/aem-utils';
import { isEmptyObject } from '../../utils/props-validator';

interface ILiveblogContainer extends IModelWrapper<ILiveblog> {}

const POSTS_PER_PAGE_FALLBACK = 10;

const LiveblogContainer: React.FC<ILiveblogContainer> = ({ query, initialModel, config = {} }) => {
    const postPerPageConfig: CommonTypingsDS.IPostsPerPage = {
        mobile: config.postsPerPageMobile || POSTS_PER_PAGE_FALLBACK,
        desktop: config.postsPerPageDesktop || POSTS_PER_PAGE_FALLBACK,
    };

    const [newPostsBadgeCounter, setNewPostsBadgeCounter] = useState<number>(0);
    const [newPostsLabelCounter, setNewPostsLabelCounter] = useState<number>(0);
    const [acceptedCookiesConfig, setAcceptedCookiesConfig] = useState<AcceptedCookiesConfig>({});

    const { dateNow } = useDateNow();
    const { pageTitle } = usePageTitle();
    const { model } = useService({
        live: config?.live,
        query,
        widget: liveblogContainerWidget,
        initialValue: initialModel,
        setNewPostsBadgeCounter,
        setNewPostsLabelCounter,
    });

    const hasInitialModel = !isEmptyObject(initialModel);
    const isLoading = isEmptyObject(model);

    // Listen to CMP changes on vendors (skip it on local env, author mode on AEM & web-view)
    useEffect(() => {
        vendorsConfigs.forEach((vendorConfig) => {
            if (isLocalDomain() || isAuthorMode() || config?.isWebView) {
                setAcceptedCookiesConfig((prevCookiesAllowed) => ({
                    ...prevCookiesAllowed,
                    [vendorConfig.name]: true,
                }));
            } else {
                skyCMP.onChange(vendorConfig.id, (consent) => {
                    setAcceptedCookiesConfig((prevCookiesAllowed) => ({
                        ...prevCookiesAllowed,
                        [vendorConfig.name]: consent,
                    }));
                });
            }
        });
    }, []);

    const newPostsConfig: INewPosts = {
        badgeCounter: newPostsBadgeCounter,
        labelCounter: newPostsLabelCounter,
        handleBadgeClick: () => {
            smoothScrollToTop();
            setNewPostsBadgeCounter(0);
            setDocumentTitle(pageTitle, 0);
        },
        isBadgeShown: !!newPostsBadgeCounter,
    };

    if (!hasInitialModel && isLoading) {
        return null;
    }

    return (
        <Liveblog
            {...model}
            tenant={model?.tenant}
            dateNow={dateNow}
            newPostsConfig={newPostsConfig}
            acceptedCookiesConfig={acceptedCookiesConfig}
            isWebView={config?.isWebView}
            postsPerPageConfig={postPerPageConfig}
        />
    );
};

export default LiveblogContainer;
